import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, RefreshCw, Skull } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import { countryFlag, countryName } from '../lib/countryFlag'

interface SupplyHit  { service_id: number; service_name: string; match_kind: string; matched_on?: string[] }
interface CarrierHit { carrier: string; carrier_name: string; title: string; url: string; published_at: string | null }
interface Victim {
  org_name: string
  group: string
  sector: string | null
  discovered_at: string | null
  leak_url: string | null
  description: string | null
  supply_chain_hits:  SupplyHit[]
  carrier_advisories: CarrierHit[]
}
interface CountryDetail {
  country: string
  country_name: string
  days: number
  total_victims: number
  victims: Victim[]
  by_group:  { group: string; victims: number }[]
  by_sector: { sector: string; victims: number }[]
  timeline:  { date: string; victims: number }[]
}

export default function RansomwareCountryDetailPage() {
  const { country = '' } = useParams<{ country: string }>()
  const [data, setData] = useState<CountryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get(`/api/overlay/ransomware-geo/country/${encodeURIComponent(country)}`, {
      params: { days, limit: 500 },
    }).then(r => { if (!cancelled) setData(r.data) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [country, days])

  // Aggregate cross-links across all victims for the side panels.
  const aggregatedSC = useMemo(() => {
    if (!data) return [] as { service_id: number; service_name: string; victims: number }[]
    const m = new Map<number, { service_id: number; service_name: string; victims: number }>()
    for (const v of data.victims) {
      for (const h of v.supply_chain_hits) {
        const existing = m.get(h.service_id)
        if (existing) existing.victims += 1
        else m.set(h.service_id, { service_id: h.service_id, service_name: h.service_name, victims: 1 })
      }
    }
    return [...m.values()].sort((a, b) => b.victims - a.victims)
  }, [data])

  const aggregatedCarriers = useMemo(() => {
    if (!data) return [] as CarrierHit[]
    const seen = new Map<string, CarrierHit>()
    for (const v of data.victims) {
      for (const a of v.carrier_advisories) {
        const key = `${a.carrier}|${a.title}`
        if (!seen.has(key)) seen.set(key, a)
      }
    }
    return [...seen.values()].sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''))
  }, [data])

  const distinctOrgs = useMemo(() => {
    if (!data) return 0
    return new Set(data.victims.map(v => v.org_name.toLowerCase())).size
  }, [data])

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Link to="/app/ransomware-geo" className="text-xs text-blue-light hover:underline inline-flex items-center gap-1 mb-1">
            <ArrowLeft size={11} /> Back to map
          </Link>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <span className="text-3xl">{countryFlag(country) || <Skull size={24} className="text-purple-300 inline" />}</span>
            {data?.country_name || countryName(country)}
            <span className="text-xs font-mono text-rose bg-rose/10 px-1.5 py-0.5 rounded ml-2">
              ransomware drilldown
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            aria-label="Time window"
            title="Time window"
            value={days}
            onChange={e => setDays(parseInt(e.target.value, 10))}
            className="input py-1.5 text-xs"
          >
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
          </select>
          <RefreshCw size={11} className={loading ? 'animate-spin' : 'opacity-30'} />
        </div>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Victims (window)" value={data.total_victims} tone="rose" />
            <Tile label="Top group"        value={data.by_group[0]?.victims || 0}  sub={data.by_group[0]?.group || '—'}  tone="amber" />
            <Tile label="Top sector"       value={data.by_sector[0]?.victims || 0} sub={data.by_sector[0]?.sector || '—'} tone="purple" />
            <Tile label="Distinct orgs"    value={distinctOrgs} tone="blue" />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="card p-3">
              <h3 className="text-xs font-semibold text-text-primary mb-2">Daily timeline</h3>
              <Sparkline data={data.timeline} />
            </div>
            <div className="card p-3">
              <h3 className="text-xs font-semibold text-text-primary mb-2">Top groups</h3>
              <BarChart rows={data.by_group.slice(0, 8).map(g => ({ label: g.group, value: g.victims }))} />
            </div>
          </div>

          {/* Main grid: table + sidebar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 card p-3">
              <h3 className="text-xs font-semibold text-text-primary mb-2">Victims</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-surface-hover text-[10px] uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-left px-2 py-1.5">Org</th>
                      <th className="text-left px-2 py-1.5">Group</th>
                      <th className="text-left px-2 py-1.5">Sector</th>
                      <th className="text-left px-2 py-1.5">Description</th>
                      <th className="text-center px-2 py-1.5" title="Supply-chain exposure">SC</th>
                      <th className="text-center px-2 py-1.5" title="Carrier advisories">CA</th>
                      <th className="text-center px-2 py-1.5">Leak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.victims.map((v, i) => (
                      <tr key={`${v.org_name}-${i}`} className="border-t border-border hover:bg-surface-hover">
                        <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted whitespace-nowrap">
                          {v.discovered_at ? v.discovered_at.slice(0, 10) : '—'}
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-[180px]" title={v.org_name}>{v.org_name || '—'}</td>
                        <td className="px-2 py-1.5">
                          <Link to={`/app/threat-actors?q=${encodeURIComponent(v.group)}`} className="text-amber-light hover:underline font-mono text-[10px]">
                            {v.group || 'unknown'}
                          </Link>
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-[120px] text-text-secondary text-[10px]" title={v.sector || ''}>
                          {v.sector || '—'}
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-[260px] text-text-secondary text-[10px]" title={v.description || ''}>
                          {v.description ? (v.description.length > 80 ? v.description.slice(0, 80) + '…' : v.description) : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {v.supply_chain_hits.length > 0
                            ? <span
                                className="inline-block w-2 h-2 rounded-full bg-rose"
                                title={v.supply_chain_hits.map(h => h.service_name).join(', ')}
                              />
                            : <span className="text-text-muted opacity-40">·</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {v.carrier_advisories.length > 0
                            ? <span
                                className="inline-block w-2 h-2 rounded-full bg-purple"
                                title={v.carrier_advisories.map(a => a.title).join(', ')}
                              />
                            : <span className="text-text-muted opacity-40">·</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {v.leak_url
                            ? <a href={v.leak_url} target="_blank" rel="noreferrer" className="text-blue-light hover:underline" aria-label="Leak post">
                                <ExternalLink size={11} className="inline" />
                              </a>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {data.victims.length === 0 && (
                      <tr><td colSpan={8} className="text-center text-[10px] italic text-text-muted py-3">No victims in this window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-3">
              {/* Supply-chain card */}
              <div className="card p-3">
                <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-rose" />
                  Supply-chain exposure
                </h3>
                {aggregatedSC.length === 0 ? (
                  <p className="text-[10px] text-text-muted italic">
                    No service-catalog matches. Either no exposure or victims don&apos;t name-match our vendors.
                  </p>
                ) : (
                  <ul className="space-y-1 text-[11px]">
                    {aggregatedSC.map(s => (
                      <li key={s.service_id} className="flex items-center justify-between gap-2">
                        <Link to="/app/services" className="text-text-primary hover:text-blue-light hover:underline truncate">
                          {s.service_name}
                        </Link>
                        <span className="font-mono text-[10px] text-rose">{s.victims}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Carrier advisories card */}
              <div className="card p-3">
                <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple" />
                  Carrier advisories
                </h3>
                {aggregatedCarriers.length === 0 ? (
                  <p className="text-[10px] text-text-muted italic">
                    No matching carrier advisories in the cached underwriting feed.
                  </p>
                ) : (
                  <ul className="space-y-1 text-[11px]">
                    {aggregatedCarriers.slice(0, 5).map((a, i) => (
                      <li key={`${a.carrier}-${i}`}>
                        <a href={a.url} target="_blank" rel="noreferrer" className="text-text-primary hover:text-blue-light hover:underline">
                          [{a.carrier_name}] {a.title}
                        </a>
                        {a.published_at && (
                          <span className="text-[9px] text-text-muted ml-1">{a.published_at.slice(0, 10)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <Link to="/app/underwriting" className="text-[10px] text-blue-light hover:underline mt-2 inline-block">
                  Open underwriting feed →
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <p className="text-sm text-text-muted italic">No data available.</p>
      )}
    </div>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: number | string; sub?: string; tone: string }) {
  const t =
    tone === 'rose'    ? 'text-rose' :
    tone === 'amber'   ? 'text-amber-light' :
    tone === 'purple'  ? 'text-purple-light' :
    tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t)}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div className="text-[10px] text-text-muted mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  )
}

// ── Inline SVG charts (no new dep) ─────────────────────────────────────────

function Sparkline({ data }: { data: { date: string; victims: number }[] }) {
  if (data.length === 0) return <p className="text-[10px] text-text-muted italic">No data.</p>
  const w = 360, h = 80, pad = 4
  const max = Math.max(1, ...data.map(d => d.victims))
  const step = (w - pad * 2) / Math.max(1, data.length - 1)
  const pts = data.map((d, i) => {
    const x = pad + i * step
    const y = h - pad - (d.victims / max) * (h - pad * 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
        <polyline
          fill="none"
          stroke="#f43f5e"
          strokeWidth="1.5"
          points={pts}
        />
      </svg>
      <div className="flex items-center justify-between text-[9px] text-text-muted font-mono mt-1">
        <span>{data[0]?.date}</span>
        <span>peak {max}/d</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  )
}

function BarChart({ rows }: { rows: { label: string; value: number }[] }) {
  if (rows.length === 0) return <p className="text-[10px] text-text-muted italic">No data.</p>
  const max = Math.max(1, ...rows.map(r => r.value))
  return (
    <div className="space-y-1">
      {rows.map(r => (
        <div key={r.label} className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-text-secondary w-20 truncate" title={r.label}>{r.label}</span>
          <div className="flex-1 bg-surface-hover h-3 rounded relative overflow-hidden">
            <div
              className="h-full bg-amber"
              style={{ width: `${(r.value / max) * 100}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-amber-light w-8 text-right">{r.value}</span>
        </div>
      ))}
    </div>
  )
}
